import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { PRODUCTS } from "@/data/products";
import { useLang } from "@/i18n/LanguageContext";
import { MDParticle } from "@/components/MDParticle";

// API endpoint — relative path.
// • Emergent preview : routed by ingress to FastAPI on :8001
// • Cloudflare Pages prod : intercepted by functions/api/contact.js
const API = "/api";

const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const f = c.form;

  const [params] = useSearchParams();
  const initialPiece = params.get("piece") || "";
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    nom: "",
    email: "",
    ville_pays: "",
    objet: c.objects[0],
    piece_interest: initialPiece,
    message: "",
  });

  useEffect(() => {
    if (initialPiece) {
      setForm((prev) => ({ ...prev, piece_interest: initialPiece }));
    }
  }, [initialPiece]);

  // Keep "objet" valid when language changes (or when the list of options
  // itself changes across releases). Falls back to the first option.
  useEffect(() => {
    setForm((prev) =>
      c.objects.includes(prev.objet) ? prev : { ...prev, objet: c.objects[0] }
    );
  }, [c.objects]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.nom || !form.email || !form.message) {
      toast.error(c.toastErrorRequired);
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data?.status === "received") {
        setSubmitted(true);
        toast.success(c.toastSuccess);
      } else {
        toast.error(c.toastErrorGeneric);
      }
    } catch (err) {
      console.error(err);
      toast.error(c.toastErrorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO ----------------------------------------------------------- */}
      <section className="pt-16 md:pt-28 pb-16 md:pb-20 relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-baseline gap-6 mb-10">
            <span className="font-eyebrow text-[hsl(var(--md-stone))]">{c.eyebrow}</span>
            <span className="hairline flex-1 max-w-[180px] mt-3" />
            <span className="font-eyebrow text-[hsl(var(--md-stone))] hidden md:inline">{c.byAppointment}</span>
          </div>
          <h1 className="display-hero text-[hsl(var(--md-ink))] max-w-[16ch] leading-[1.02]">
            {c.title}
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-12">
            <div className="md:col-span-7 max-w-[58ch]">
              <p className="editorial text-[hsl(var(--md-ink))]">
                {c.intro}
              </p>
              <a
                href="mailto:contact@maisondodji.com"
                data-testid="hero-email"
                className="font-display text-[1.5rem] md:text-[1.75rem] mt-6 inline-block text-[hsl(var(--md-ink))] hover:text-[hsl(var(--md-stone))] transition-colors"
              >
                contact@maisondodji.com
              </a>
            </div>
            <p className="md:col-span-4 md:col-start-9 editorial-muted">
              {c.asideL1}<br />
              {c.asideL2}
            </p>
          </div>
        </div>
      </section>

      {/* PROCESS -------------------------------------------------------- */}
      <section
        data-testid="contact-process"
        className="bg-[hsl(var(--md-bone))] border-y border-[hsl(var(--md-line))] py-20 md:py-24 relative overflow-hidden"
      >
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-12">{c.processEyebrow}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10">
            {c.steps.map((step) => (
              <div key={step.num} className="flex gap-6">
                <span className="font-display font-light text-[2.5rem] text-[hsl(var(--md-stone))] leading-none min-w-[64px]">
                  {step.num}
                </span>
                <div>
                  <h3 className="font-display text-[1.45rem] text-[hsl(var(--md-ink))] leading-tight">
                    {step.title}
                  </h3>
                  <p className="editorial-muted mt-3 text-[0.98rem]">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT GRID --------------------------------------------------- */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
          {/* CONTACT ASIDE — email & url only */}
          <aside className="lg:col-span-4" data-testid="contact-aside">
            <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-8">{c.asideEyebrow}</p>

            <div>
              <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-3">{c.writeEyebrow}</p>
              <a
                href="mailto:contact@maisondodji.com"
                data-testid="contact-email"
                className="font-display text-[1.4rem] md:text-[1.5rem] text-[hsl(var(--md-ink))] hover:text-[hsl(var(--md-stone))] transition-colors"
              >
                contact@maisondodji.com
              </a>
              <p className="font-body text-[14px] mt-5">
                <a
                  href="https://www.maisondodji.com"
                  data-testid="contact-website"
                  className="text-[hsl(var(--md-stone))] hover:text-[hsl(var(--md-ink))] transition-colors"
                >
                  www.maisondodji.com
                </a>
              </p>
            </div>

            {/* Private Order PDF — discreet download */}
            <div className="mt-14 pt-10 border-t border-[hsl(var(--md-line))]">
              <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-3">
                {c.privateOrderEyebrow}
              </p>
              <a
                href="/downloads/Maison_Dodji_Private_Order_Customisation_FR_EN.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-private-order-pdf"
                className="font-display text-[1.15rem] md:text-[1.2rem] text-[hsl(var(--md-ink))] hover:text-[hsl(var(--md-stone))] transition-colors inline-flex items-center gap-2"
              >
                {c.privateOrderPdfLabel}
                <MDParticle />
              </a>
              <p className="font-eyebrow text-[hsl(var(--md-stone))] mt-3 text-[10px]">
                {c.privateOrderPdfHint}
              </p>
            </div>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-7 lg:col-start-6">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))] p-10 md:p-14"
              >
                <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-6">{c.successEyebrow}</p>
                <h2 className="display-2 text-[hsl(var(--md-ink))]">
                  {c.successTitle}
                </h2>
                <p className="editorial mt-6 text-[hsl(var(--md-ink))]">
                  {c.successBody}
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="space-y-10"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div>
                    <label htmlFor="nom" className="label-quiet">{f.nom}</label>
                    <input
                      id="nom"
                      name="nom"
                      type="text"
                      required
                      value={form.nom}
                      onChange={onChange}
                      data-testid="contact-input-nom"
                      placeholder={f.nomPlaceholder}
                      className="input-field mt-3"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="label-quiet">{f.email}</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      data-testid="contact-input-email"
                      placeholder={f.emailPlaceholder}
                      className="input-field mt-3"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div>
                    <label htmlFor="ville_pays" className="label-quiet">{f.ville}</label>
                    <input
                      id="ville_pays"
                      name="ville_pays"
                      type="text"
                      value={form.ville_pays}
                      onChange={onChange}
                      data-testid="contact-input-ville"
                      placeholder={f.villePlaceholder}
                      className="input-field mt-3"
                    />
                  </div>
                  <div>
                    <label htmlFor="objet" className="label-quiet">{f.objet}</label>
                    <select
                      id="objet"
                      name="objet"
                      value={form.objet}
                      onChange={onChange}
                      data-testid="contact-input-objet"
                      className="input-field mt-3 appearance-none bg-transparent cursor-pointer"
                    >
                      {c.objects.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="piece_interest" className="label-quiet">{f.piece}</label>
                  <select
                    id="piece_interest"
                    name="piece_interest"
                    value={form.piece_interest}
                    onChange={onChange}
                    data-testid="contact-input-piece"
                    className="input-field mt-3 appearance-none bg-transparent cursor-pointer"
                  >
                    <option value="">{f.pieceNoPreference}</option>
                    {PRODUCTS.map((p) => (
                      <option key={p.slug} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="label-quiet">{f.message}</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    value={form.message}
                    onChange={onChange}
                    data-testid="contact-input-message"
                    placeholder={f.messagePlaceholder}
                    className="input-field mt-3 resize-none"
                  />
                </div>

                <div className="hairline" />

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-2">
                  <p className="font-eyebrow text-[hsl(var(--md-stone))] max-w-[44ch]">
                    {f.note}
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="btn-primary disabled:opacity-60"
                  >
                    {submitting ? f.submitting : f.submit}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CLOSING ------------------------------------------------------- */}
      <section className="bg-[hsl(var(--md-sand))]/40 border-t border-[hsl(var(--md-line))] py-24 md:py-32 relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-8">{c.closingEyebrow}</p>
            <h2 className="display-1 text-[hsl(var(--md-ink))] leading-[1.05]">
              {c.closingTitle}
            </h2>
            <p className="editorial-muted mt-8 max-w-[52ch] mx-auto">
              {c.closingBody}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
